import axios from 'axios';

// =============================================================================
// API BASE URL CONFIGURATION
// =============================================================================
// IMPORTANT: In production, REACT_APP_BACKEND_URL must be set in Vercel env vars.
// NEVER fallback to window.location.origin - this causes silent API failures.

const getBackendUrl = () => {
  // Check for env var (primary)
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  
  if (envUrl) {
    // Force HTTPS in production; keep http for localhost dev
    const isLocalhost = envUrl.includes('localhost') || envUrl.includes('127.0.0.1');
    const cleanUrl = (isLocalhost ? envUrl : envUrl.replace(/^http:/, 'https:')).replace(/\/$/, '');
    console.log('[API] Backend URL from env:', cleanUrl);
    return cleanUrl;
  }
  
  // In development, allow localhost
  if (process.env.NODE_ENV === 'development') {
    console.warn('[API] REACT_APP_BACKEND_URL not set, using localhost:8001 for development');
    return 'http://localhost:8001';
  }
  
  // In production, this is a configuration error - do NOT fallback to window.origin
  console.error('[API] CRITICAL: REACT_APP_BACKEND_URL not set in production! API calls will fail.');
  console.error('[API] Set REACT_APP_BACKEND_URL in Vercel Environment Variables.');
  
  // Return a clearly broken URL so errors are obvious
  return 'https://BACKEND_URL_NOT_CONFIGURED';
};

const BACKEND_URL = getBackendUrl();
export const API = `${BACKEND_URL}/api`;

// Log once at startup
console.log('[API] Base URL:', API);

// Check if backend is configured (for UI banner)
export const isBackendConfigured = () => {
  return !BACKEND_URL.includes('NOT_CONFIGURED');
};

const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available and ensure HTTPS
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // Force HTTPS in production
  if (config.baseURL && config.baseURL.startsWith('http://') && process.env.NODE_ENV === 'production') {
    config.baseURL = config.baseURL.replace('http://', 'https://');
  }
  if (config.url && config.url.startsWith('http://') && process.env.NODE_ENV === 'production') {
    config.url = config.url.replace('http://', 'https://');
  }
  
  return config;
});

// Auto-logout on 401 — expired or invalid tokens should not leave the user stuck
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('user_role');
      localStorage.removeItem('family_id');
      localStorage.removeItem('parent_id');
      if (window.location.pathname !== '/login' && window.location.pathname !== '/signup') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const familiesAPI = {
  getAll: () => api.get('/families'),
  getById: (id) => api.get(`/families/${id}`),
  create: (data) => api.post('/families', data),
  update: (id, data) => api.put(`/families/${id}`, data),
  delete: (id) => api.delete(`/families/${id}`),
};

export const childrenAPI = {
  getByFamily: (familyId) => api.get(`/children/family/${familyId}`),
  getById: (id) => api.get(`/children/${id}`),
  create: (data) => api.post('/children/', data),
  update: (id, data) => api.put(`/children/${id}/`, data),
  delete: (id) => api.delete(`/children/${id}/`, data),
};

export const caregiversAPI = {
  getAll: () => api.get('/caregivers/'),
  getByFamily: (familyId) => api.get(`/caregivers/family/${familyId}`),
  getById: (id) => api.get(`/caregivers/${id}`),
  create: (data) => api.post('/caregivers/', data),
  update: (id, data) => api.put(`/caregivers/${id}/`, data),
  delete: (id) => api.delete(`/caregivers/${id}`),
};

export const parentsAPI = {
  getAll: () => api.get('/parents'),
  create: (data) => api.post('/parents', data),
  update: (id, data) => api.put(`/parents/${id}`, data),
  delete: (id) => api.delete(`/parents/${id}`),
};

export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  signup: (data) => api.post('/auth/signup', data),
  login: (data) => api.post('/auth/login', data),
  googleAuth: (data) => api.post('/auth/google', data),
  getCurrentParent: () => api.get('/auth/me'),
};

// ============================================================================
// GMAIL OAUTH APIs
// ============================================================================
export const gmailAPI = {
  // Start OAuth flow - returns auth_url
  startAuth: (familyId) => api.get(`/auth/google/start?family_id=${familyId}`),
  
  // Get connections for a family
  getConnections: (familyId) => api.get(`/auth/google/connections/${familyId}`),
  
  // Disconnect a Gmail account
  disconnect: (connectionId, familyId) => 
    api.post(`/auth/google/disconnect?connection_id=${connectionId}&family_id=${familyId}`),
  
  // Manual poll (for testing)
  triggerPoll: (connectionId) => 
    api.post(`/auth/google/poll${connectionId ? `?connection_id=${connectionId}` : ''}`),
  
  // Gmail Watch (Push) endpoints
  startWatch: (familyId, connectionId) =>
    api.post(`/gmail/watch/start/${familyId}?connection_id=${connectionId}`),
  
  stopWatch: (familyId, connectionId) =>
    api.post(`/gmail/watch/stop/${familyId}?connection_id=${connectionId}`),
  
  getWatchStatus: (familyId, connectionId) =>
    api.get(`/gmail/watch/status/${familyId}?connection_id=${connectionId}`),
};

export const checkinsAPI = {
  getAll: () => api.get('/checkins'),
  getById: (id) => api.get(`/checkins/${id}`),
  trigger: (data) => api.post('/checkins/trigger', data),
};

export const alertsAPI = {
  getAll: () => api.get('/alerts'),
};

export const logsAPI = {
  getAll: () => api.get('/logs'),
};

export const digestsAPI = {
  getAll: () => api.get('/digests'),
};

export const aiSummariesAPI = {
  generate: (data) => api.post('/ai-summaries/generate', data),
  getByChild: (childId) => api.get(`/ai-summaries/child/${childId}`),
};

export const schoolTasksAPI = {
  getByChild: (childId) => api.get(`/school-tasks/child/${childId}`),
  getPending: (childId) => api.get(`/school-tasks/child/${childId}/pending`),
  create: (data) => api.post('/school-tasks', data),
  update: (id, data) => api.put(`/school-tasks/${id}`, data),
  delete: (id) => api.delete(`/school-tasks/${id}`),
  webhook: (data) => api.post('/school-tasks/webhook', data),
};

export const statsAPI = {
  get: () => api.get('/stats'),
};

// ============================================================================
// OPERATOR DASHBOARD APIs
// ============================================================================
export const operatorAPI = {
  // Overview & KPIs
  getOverview: () => api.get('/operator/overview'),
  getKpis: () => api.get('/operator/kpis'),
  getFunnel: () => api.get('/operator/funnel'),
  getSegments: () => api.get('/operator/segments'),
  getToday: () => api.get('/operator/today'),
  
  // Families
  getFamilies: (limit = 100) => api.get(`/operator/families?limit=${limit}`),
  getActiveFamilies: (limit = 50) => api.get(`/operator/families/active?limit=${limit}`),
  getChurnRiskFamilies: (limit = 50) => api.get(`/operator/families/churn-risk?limit=${limit}`),
  
  // Family Drilldown
  getFamily: (familyId) => api.get(`/operator/family/${familyId}`),
  getFamilyChecklist: (familyId) => api.get(`/operator/family/${familyId}/checklist`),
  recomputeScore: (familyId) => api.post(`/operator/family/${familyId}/compute-score`),
  
  // Caregiver Language
  updateCaregiverLanguage: (caregiverId, language) => 
    api.put(`/operator/caregiver/${caregiverId}/language?language=${language}`),
  
  // Queue & Failures
  getUnsent: (limit = 50) => api.get(`/operator/queue/unsent-packs?limit=${limit}`),
  getFailures: (limit = 20) => api.get(`/operator/debug/failures?limit=${limit}`),
  retryDelivery: (deliveryId) => api.post(`/operator/retry/${deliveryId}`),
  
  // Debug
  getPipelineStatus: () => api.get('/operator/debug/pipeline-status'),
  debugEmailRouting: (email) => api.get(`/operator/debug/email-routing?email=${encodeURIComponent(email)}`),
  
  // Pilot Mode: Test Email & Resend
  testHeritageEmail: (familyId, data) => api.post(`/operator/family/${familyId}/test-email`, data),
  resendTodaysPlan: (familyId) => api.post(`/operator/family/${familyId}/resend-plan`),
  
  // Reliability Dashboard
  getRecentCards: (limit = 20, familyName = null) => {
    let url = `/operator/reliability/cards?limit=${limit}`;
    if (familyName) url += `&family_name=${encodeURIComponent(familyName)}`;
    return api.get(url);
  },
  getRecentPlans: (limit = 20, familyName = null) => {
    let url = `/operator/reliability/plans?limit=${limit}`;
    if (familyName) url += `&family_name=${encodeURIComponent(familyName)}`;
    return api.get(url);
  },
  
  // Execution Logs
  getExecutionLogs: (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.family_id) searchParams.append('family_id', params.family_id);
    if (params.log_type) searchParams.append('log_type', params.log_type);
    if (params.start_date) searchParams.append('start_date', params.start_date);
    if (params.end_date) searchParams.append('end_date', params.end_date);
    searchParams.append('limit', (params.limit || 50).toString());
    searchParams.append('offset', (params.offset || 0).toString());
    return api.get(`/operator/execution-logs?${searchParams}`);
  },
  
  // Scheduler Status
  getSchedulerStatus: () => api.get('/operator/scheduler/status'),
  
  // Full Day Simulation
  runFullDaySimulation: () => api.post('/operator/test-p2-jobs/full-day-simulation'),
  
  // Test Data Cleanup
  previewTestDataCleanup: () => api.get('/operator/test-data/preview'),
  cleanupTestData: (confirm) => api.post('/operator/test-data/cleanup', { confirm }),
  markFamilyAsTest: (familyId, isTest) => api.post(`/operator/family/${familyId}/mark-test?is_test=${isTest}`),
};

// ============================================================================
// SCHOOLS & GRADES APIs
// ============================================================================
export const schoolsAPI = {
  // Get all schools (searchable)
  getSchools: (search = '', city = '') => {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (city) params.append('city', city);
    return api.get(`/schools?${params}`);
  },
  
  // Get all grades
  getGrades: () => api.get('/schools/grades'),

  // School branches + curriculum tracks — Personalization V1
  getBranches: (schoolId) => api.get(`/schools/branches/${schoolId}`),
  getCurriculumTracks: (branchId) => api.get(`/schools/tracks/${branchId}`),
  updateChildCurriculumTrack: (childId, trackId) =>
    api.put(`/schools/child/${childId}/curriculum-track?track_id=${trackId}`),
  
  // Get family's current school/grade
  getFamilySchool: (familyId) => api.get(`/schools/family/${familyId}`),
  
  // Update family's school/grade
  updateFamilySchool: (familyId, data) => api.put(`/schools/family/${familyId}`, data),
  
  // =========================================================
  // CHILD-LEVEL SCHOOL MANAGEMENT (Phase 3)
  // =========================================================
  
  // Get all children for family with their school info
  getChildrenWithSchools: (familyId) => api.get(`/schools/children/${familyId}`),
  
  // Get school info for a specific child
  getChildSchool: (childId) => api.get(`/schools/child/${childId}`),
  
  // Update school/grade for a child
  updateChildSchool: (childId, data) => api.put(`/schools/child/${childId}`, data),
  
  // Set child as primary school child
  setPrimaryChild: (childId) => api.post(`/schools/child/${childId}/set-primary`),
  
  // =========================================================
  // SENDER ALLOWLIST (Phase 3)
  // =========================================================
  
  // Get allowlist for a school
  getAllowlist: (schoolDirectoryId) => api.get(`/schools/allowlist/${schoolDirectoryId}`),
  
  // Add entry to allowlist
  addAllowlistEntry: (schoolDirectoryId, data) => api.post(`/schools/allowlist/${schoolDirectoryId}`, data),
  
  // Remove entry from allowlist
  removeAllowlistEntry: (entryId) => api.delete(`/schools/allowlist/entry/${entryId}`),
  
  // Validate sender against school's allowlist
  validateSender: (schoolDirectoryId, email) => 
    api.post(`/schools/allowlist/${schoolDirectoryId}/validate?sender_email=${encodeURIComponent(email)}`),
};

// ============================================================================
// PARENT DASHBOARD APIs
// ============================================================================
export const parentAPI = {
  // New aggregated endpoints for smart dashboard
  getToday: (familyId) => api.get(`/parent/today/${familyId}`),
  getWeek: (familyId, days = 7) => api.get(`/parent/week/${familyId}?days=${days}`),
  
  // Home (legacy)
  getHome: (familyId) => api.get(`/parent/home/${familyId}`),
  
  // Child Details
  getChild: (childId) => api.get(`/parent/child/${childId}`),
  
  // Settings
  getSettings: (familyId) => api.get(`/parent/settings/${familyId}`),
  updateSettings: (familyId, settings) => api.put(`/parent/settings/${familyId}`, settings),
  updateCaregiverLanguage: (familyId, caregiverId, language) => 
    api.put(`/parent/settings/${familyId}/caregiver/${caregiverId}/language?language=${language}`),
  
  // Email Verification
  verifyEmail: (familyId, email) => api.post(`/parent/verify-email/${familyId}`, { email }),
  
  // Tasks
  getTasks: (familyId, status = null) => {
    let url = `/parent/tasks/${familyId}`;
    if (status) url += `?status=${status}`;
    return api.get(url);
  },
  updateTaskStatus: (taskId, status) => api.put(`/parent/tasks/${taskId}/status?status=${status}`),

  // Child Routines
  updateChildRoutines: (childId, routines) => api.put(`/parent/children/${childId}/routines`, { routines }),

  // Child Profile (interests + energy_level) — Personalization V1
  updateChildProfile: (childId, profile) => api.put(`/parent/children/${childId}/profile`, { profile }),

  // Child Pickup Configuration — Multi-child Phase 1
  // pickup_time: 'HH:MM' (24-hour IST) or null to clear
  // is_pickup_eligible: true/false/null
  updateChildPickup: (childId, { pickupTime, isPickupEligible, transportMode }) =>
    api.put(`/parent/children/${childId}/pickup`, {
      pickup_time: pickupTime ?? undefined,
      is_pickup_eligible: isPickupEligible ?? undefined,
      transport_mode: transportMode ?? undefined,
    }),

  // Caregiver-to-child assignment — Multi-child Phase 1
  // childIds: array of child UUIDs the caregiver is responsible for
  assignCaregiverChildren: (caregiverId, childIds) =>
    api.put(`/parent/caregivers/${caregiverId}/children`, { child_ids: childIds }),

  // Caregiver Experience — Personalization V1
  updateCaregiverExperience: (caregiverId, experienceLevel) =>
    api.put(`/parent/caregivers/${caregiverId}/experience`, { experience_level: experienceLevel }),

  // Parent Preferences — Personalization V1
  updateParentPreferences: (familyId, preferences) =>
    api.put(`/parent/preferences/${familyId}`, { preferences }),

  // Family Parents — alert preferences (migration 008)
  getFamilyParents: (familyId) => api.get(`/parent/family/${familyId}/parents`),
  updateParentAlertPrefs: (parentId, prefs) =>
    api.put(`/parent/parents/${parentId}/alert-preferences`, prefs),

  // Family-level preferences (notification timing, verbosity)
  getFamilyPreferences: (familyId) =>
    api.get(`/parent/family/${familyId}/preferences`),
  updateFamilyPreferences: (familyId, preferences) =>
    api.put(`/parent/family/${familyId}/preferences`, { preferences }),

  // Caregiver assigned children — seed assignment UI initial state
  getCaregiverAssignedChildren: (caregiverId) =>
    api.get(`/parent/caregivers/${caregiverId}/children`),

  // Emails
  getEmails: (familyId, limit = 20) => api.get(`/parent/emails/${familyId}?limit=${limit}`),
  
  // School Cards
  getCards: (familyId, limit = 20) => api.get(`/parent/cards/${familyId}?limit=${limit}`),
  markCardComplete: (cardId) => api.post(`/parent/cards/${cardId}/complete`),
  
  // Timeline
  getTimeline: (familyId, date = null) => {
    let url = `/parent/timeline/${familyId}`;
    if (date) url += `?date=${date}`;
    return api.get(url);
  },

  // Daily Plans
  getDailyPlan: (familyId, date = null) => {
    let url = `/parent/daily-plan/${familyId}`;
    if (date) url += `?date=${date}`;
    return api.get(url);
  },
  getRecentPlans: (familyId, limit = 7) => api.get(`/parent/daily-plans/${familyId}/recent?limit=${limit}`),
  updateChecklistItem: (familyId, itemId, status, notes = null) => 
    api.post(`/parent/daily-plan/${familyId}/checklist/${itemId}`, { status, notes }),
  
  // Onboarding
  getOnboardingStatus: (familyId) => api.get(`/parent/onboarding/${familyId}`),
  advanceOnboarding: (familyId, step) => api.post(`/parent/onboarding/${familyId}/step/${step}`),
  completeOnboarding: (familyId) => api.post(`/parent/onboarding/${familyId}/complete`),
  
  // Tomorrow Status (Holiday Banner)
  getTomorrowStatus: (familyId) => api.get(`/parent/school-calendar/tomorrow/${familyId}`),
};

// ============================================================================
// ADMIN APIs (Schools, Migrations)
// ============================================================================
export const adminAPI = {
  // Schools
  getSchools: (familyId = null) => {
    let url = '/admin/schools';
    if (familyId) url += `?family_id=${familyId}`;
    return api.get(url);
  },
  createSchool: (data) => api.post('/admin/schools', data),
  
  // Migrations
  getMigrations: () => api.get('/admin/migrations/all'),
  getMigrationStatus: () => api.get('/admin/migrations/status'),
  
  // Debug
  getLastEmail: () => api.get('/debug/last-email'),
  getLastNannyPack: (childId = null) => {
    let url = '/debug/last-nanny-pack';
    if (childId) url += `?child_id=${childId}`;
    return api.get(url);
  },
  getTasks: (childId, status = null) => {
    let url = `/debug/tasks?child_id=${childId}`;
    if (status) url += `&status=${status}`;
    return api.get(url);
  },
  
  // Email Webhook (for testing)
  testEmailInbound: (data) => api.post('/webhooks/email/inbound', data),

  // Classes / Extracurriculars
  getClasses: (familyId) => api.get(`/parent/classes/${familyId}`),
  createClass: (familyId, data) => api.post(`/parent/classes/${familyId}`, data),
  updateClass: (familyId, classId, data) => api.put(`/parent/classes/${familyId}/${classId}`, data),
  deleteClass: (familyId, classId) => api.delete(`/parent/classes/${familyId}/${classId}`),

  // Medications
  getMedications: (familyId) => api.get(`/parent/medications/${familyId}`),
  createMedication: (familyId, data) => api.post(`/parent/medications/${familyId}`, data),
  updateMedication: (familyId, medicationId, data) => api.put(`/parent/medications/${familyId}/${medicationId}`, data),
  deleteMedication: (familyId, medicationId) => api.delete(`/parent/medications/${familyId}/${medicationId}`),
  getMedicationLogs: (familyId) => api.get(`/parent/medications/${familyId}/logs`),
};

// ============================================================================
// PAYMENT / SUBSCRIPTION APIs
// ============================================================================
export const paymentAPI = {
  subscribe: () => api.post('/payment/subscribe'),
  verify: (data) => api.post('/payment/verify', data),
  getStatus: () => api.get('/payment/status'),
  cancel: () => api.post('/payment/cancel'),
};

// ============================================================================
// WAITLIST APIs (public, no auth)
// ============================================================================
export const waitlistAPI = {
  join: (data) => api.post('/waitlist', data),
  getCount: () => api.get('/waitlist/count'),
};

export default api;
